import { createContext, useContext, useState, useCallback } from "react";
import { useRuns } from "contexts/RunsContext";
import { useAuth } from "contexts/AuthContext";

import { API_URL } from "config";
import { managedFetch } from "utils/FetchManager";
import useRun from "hooks/useRun";
import usePage from "hooks/usePage";
import { ensureIds } from "utils/map";

const SimulationsContext = createContext();

export const useSimulations = () => useContext(SimulationsContext);

async function submitSimulation(payload, AUTH_TOKEN, currentUser) {
  console.log("Submitting simulation:", payload);
  try {
    const response = await managedFetch(
      `${API_URL}/simulation`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${AUTH_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
      currentUser
    );

    if (!response) {
      console.log("Request was likely aborted");
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error("Error in submitSimulation:", error);
    throw error;
  }
}

// Unused, will need slight adapting to work here (pass variables in, or declare inside component)
// async function runElmfire() {
//   const bbox = turf.bbox(IGNITION);
//   const xx = (bbox[0] + bbox[2]) / 2;
//   const yy = (bbox[1] + bbox[3]) / 2;
//   const elmfirePayload = {
//     wind: {
//       speed: WIND_SPEED,
//       direction: WIND_DIRECTION,
//     },
//     fuel_moisture: {
//       one: 3,
//       ten: 4,
//       hund: 5,
//       herb: 3,
//       woody: 7,
//     },
//     ignition: IGNITION,
//     simulation: {
//       simulation_duration_minutes: +$("#select-duration").val() * 60,
//       ros_adjustment_factor: 1,
//       crs: "5070",
//     },
//     suppression: {
//       enable_IA: false,
//       enable_EA: false,
//     },
//     landscape_overrides: {},
//     spotting: {
//       pig: 99,
//       enable: false,
//       enable_surface_spotting: false,
//     },
//     landscape: {
//       center_latitude: yy,
//       center_longitude: xx,
//       landfire_version: "2.2.0",
//     },
//   };

//   const elmfirePerimeters = await fetchElmfire(elmfirePayload);
//   console.log(elmfirePerimeters);

//   if (map.getLayer("perimeters")) {
//     map.removeLayer("perimeters");
//   }
//   if (map.getSource("perimeters")) {
//     map.removeSource("perimeters");
//   }

//   map.addSource("perimeters", {
//     type: "geojson",
//     data: elmfirePerimeters,
//   });
//   map.addLayer({
//     type: "fill",
//     source: "perimeters",
//     id: "perimeters",
//     paint: {
//       "fill-opacity": 0.5,
//       "fill-color": [
//         "interpolate",
//         ["linear"],
//         ["get", "arrival_hours"],
//         0,
//         "firebrick",
//         +$("#select-duration").val(),
//         "orange",
//       ],
//     },
//   });
// }
// async function fetchElmfire(payload) {
//   $("#status").append(`<p>Fetching Elmfire Run for selected location...</p>`);

//   return fetch(`${serviceURL}/elmfire`, {
//     method: "POST",
//     headers: {
//       Authorization: `Bearer ${accessToken}`,
//       "Content-Type": "application/json",
//       Accept: "application/json",
//     },
//     body: JSON.stringify(payload),
//   }).then((d) => {
//     return d.json();
//   });
// }

export const SimulationsProvider = ({ children }) => {
  const {
    newRunName,
    setNewRunName,
    newRunDescription,
    setIsSimulating,
    isSimulating,
    setIsCreatingSimulation,
    reloadRecentRuns,
    runs,
  } = useRuns();
  const { currRun, setCurrRun, setRunId } = useRun();
  const { currentUser } = useAuth();
  const { projectId, isSharePage, runId } = usePage();
  const prefix = isSharePage ? "/hash" : "";
  const [runDuration, setRunDuration] = useState(1);
  const [windSpeed, setWindSpeed] = useState(10);
  const [windDirection, setWindDirection] = useState(0);
  const [relativeHumidity, setRelativeHumidity] = useState(2);
  const [hr100, setHr100] = useState(2);

  const [runDurationError, setRunDurationError] = useState(false);
  const [windSpeedError, setWindSpeedError] = useState(false);
  const [windDirectionError, setWindDirectionError] = useState(false);
  const [relativeHumidityError, setRelativeHumidityError] = useState(false);
  const [hr100Error, setHr100Error] = useState(false);

  const [fuelscapeVersion, setFuelscapeVersion] = useState("260XP");
  // Optional gs:// path to a user-supplied landscape GeoTIFF. When set, the
  // backend uses it instead of building a landscape from fuelscapeVersion.
  const [customLcpFile, setCustomLcpFile] = useState("");
  const [runEntryPoints, setRunEntryPoints] = useState(false);
  const [spotProbability, setSpotProbability] = useState(0);
  const spotProbabilityError = !(
    spotProbability !== "" &&
    spotProbability >= 0 &&
    spotProbability <= 1
  );

  const [overrideMode, setOverrideMode] = useState("nb");
  const [overrides, setOverrides] = useState(null);
  const [showOverrides, setShowOverrides] = useState(false);
  const [uploadedOverrideGeoJson, setUploadedOverrideGeoJson] = useState(null);
  // const [clearOverrides, setClearOverrides] = useState(null);

  const [ignition, setIgnition] = useState(null);
  const [clearIgnitionDrawing, setClearIgnitionDrawing] = useState(null);

  const [urbanSimToDuplicate, setUrbanSimToDuplicate] = useState(null);
  const AUTH_TOKEN = currentUser?.accessToken;

  // Validate input fields
  function validateRunDuration(value) {
    setRunDuration(value);
    if (value < 1 || value > 36) {
      setRunDurationError(true);
    } else {
      setRunDurationError(false);
    }
  }

  function validateWindSpeed(value) {
    setWindSpeed(value);
    if (value < 0 || value > 100) {
      setWindSpeedError(true);
    } else {
      setWindSpeedError(false);
    }
  }

  function validateWindDirection(value) {
    setWindDirection(value);
    if (value < 0 || value >= 360) {
      setWindDirectionError(true);
    } else {
      setWindDirectionError(false);
    }
  }

  function validateRelativeHumidity(value) {
    setRelativeHumidity(value);
    if (value < 0 || value > 75) {
      setRelativeHumidityError(true);
    } else {
      setRelativeHumidityError(false);
    }
  }

  function validateHr100(value) {
    setHr100(value);
    if (value < 0 || value > 75) {
      setHr100Error(true);
    } else {
      setHr100Error(false);
    }
  }

  function createSimulation(type) {
    setCustomLcpFile("");
    if (type === "existing") {
      if (!currRun) {
        alert("No current run to create simulation from.");
        return;
      }
      const currentRunFromList = runs.find((r) => r.id === runId);
      setRunDuration(currRun.config.simulation.duration / 60);
      setSpotProbability(currRun.config.simulation.spot_probability ?? 0);
      setWindDirection(currRun.config.wind.direction);
      setWindSpeed(currRun.config.wind.speed);
      setRelativeHumidity(currRun.config.fuel_moisture["1-hr"]);
      setHr100(currRun.config.fuel_moisture["100-hr"]);
      setIgnition(ensureIds(currRun.ignition));
      if (currRun.overrides.features.length > 0) {
        const overrides = ensureIds(currRun.overrides);
        setOverrides(overrides);
        setOverrideMode(currRun.config.override_mode);
        setShowOverrides(true);
      }
      // A custom-landscape run records its gs:// path in landscape_version, so
      // only feed a real version into the Fuelscape Version select; carry the
      // gs:// path (or an explicit custom_lcp_file) into the landscape upload
      // field instead, so it doesn't land in the select as an out-of-range value.
      const dupLandscape = currentRunFromList?.landscape_version;
      const dupCustom =
        currentRunFromList?.custom_lcp_file ||
        (typeof dupLandscape === "string" && dupLandscape.startsWith("gs://")
          ? dupLandscape
          : null);
      if (dupCustom) {
        setCustomLcpFile(dupCustom);
      }
      if (
        dupLandscape &&
        !(typeof dupLandscape === "string" && dupLandscape.startsWith("gs://"))
      ) {
        setFuelscapeVersion(dupLandscape);
      }
      setRunId(null);
      setCurrRun(null);
    } else if (type === "new") {
      if (currRun) {
        setRunId(null);
      }
    }
    setIsCreatingSimulation(true);
    console.log("Creating new simulation");
  }

  const clearSimulation = () => {
    setIsCreatingSimulation(false);
    clearIgnitionDrawing();
    setIgnition(null);
    setOverrides(null);
    setShowOverrides(false);
    setCustomLcpFile("");
    setCurrRun(null);
  };

  const runSimulation = useCallback(async () => {
    if (!ignition) {
      alert("Please draw an ignition line first.");
      return;
    }

    // await runElmfire()

    setIsSimulating(true);

    const payload = {
      name: newRunName,
      description: newRunDescription,
      created_by: currentUser.email,
      xp_project_id: projectId,
      simulation_type: "mtt",
      mtt_request: {
        wind_speed: windSpeed,
        wind_direction: windDirection,
        fuel_moisture: {
          one: relativeHumidity,
          ten: relativeHumidity,
          hund: hr100,
          herb: relativeHumidity + 2,
          woody: 70,
        },
        ignition: ignition,
        duration: runDuration * 60,
        overrides: showOverrides && overrides ? overrides : null,
        override_mode: showOverrides && overrides ? overrideMode : null,
        landscape_version: fuelscapeVersion,
        run_entry_points: runEntryPoints,
        spot_probability: Number(spotProbability),
        ...(customLcpFile && customLcpFile.trim()
          ? { custom_lcp_file: customLcpFile.trim() }
          : {}),
      },
    };

    try {
      const results = await submitSimulation(payload, AUTH_TOKEN, currentUser);

      // If results is null, the request was likely aborted (e.g., during logout)
      if (!results) {
        console.log(
          "Simulation request was likely aborted or returned no results"
        );
        return;
      }

      if (results.status === "pending") {
        setNewRunName("");
        setRunId(null);
        setCurrRun(null);
        clearIgnitionDrawing();
        clearSimulation();
        await reloadRecentRuns();
      } else if (results.detail) {
        // Backend rejected the request (e.g. the custom landscape is missing
        // or doesn't cover the ignition). Surface the reason and keep the form
        // open so the user can fix it, rather than silently failing.
        alert("Could not start simulation: " + results.detail);
      } else {
        alert("No results when running your simulation. Please try again.");
      }
    } catch (err) {
      // Don't show error message for aborted requests
      if (err.name !== "AbortError") {
        console.log("Error submitting simulation:", err);
        alert("Error submitting simulation:" + err);
      } else {
        console.log("Simulation request was aborted");
      }
    } finally {
      setIsSimulating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ignition,
    windSpeed,
    windDirection,
    relativeHumidity,
    overrides,
    showOverrides,
    hr100,
    runDuration,
    setRunId,
    clearIgnitionDrawing,
    fuelscapeVersion,
    customLcpFile,
    overrideMode,
    setCurrRun,
    setIsSimulating,
    currentUser,
    newRunName,
    newRunDescription,
    runEntryPoints,
    spotProbability,
  ]);

  async function submitSuitability(simulationId) {
    console.log("Submitting suitability for simulation ID:", simulationId);
    try {
      const response = await managedFetch(
        `${API_URL}${prefix}/simulation/suitability/${simulationId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${AUTH_TOKEN}`,
            "Content-Type": "application/json",
          },
        },
        currentUser
      );

      if (!response) {
        console.log("Request was likely aborted");
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("Error in submitSuitability:", error);
      throw error;
    }
  }

  return (
    <SimulationsContext.Provider
      value={{
        runDuration,
        setRunDuration,
        windSpeed,
        setWindSpeed,
        windDirection,
        setWindDirection,
        relativeHumidity,
        setRelativeHumidity,
        hr100,
        setHr100,
        overrideMode,
        setOverrideMode,
        overrides,
        setOverrides,
        showOverrides,
        setShowOverrides,
        fuelscapeVersion,
        setFuelscapeVersion,
        customLcpFile,
        setCustomLcpFile,

        runDurationError,
        setRunDurationError,
        windSpeedError,
        setWindSpeedError,
        windDirectionError,
        setWindDirectionError,
        relativeHumidityError,
        setRelativeHumidityError,
        hr100Error,
        setHr100Error,

        validateRunDuration,
        validateWindSpeed,
        validateWindDirection,
        validateRelativeHumidity,
        validateHr100,

        ignition,
        setIgnition,
        clearIgnitionDrawing,
        setClearIgnitionDrawing,

        runSimulation,
        isSimulating,

        submitSuitability,

        createSimulation,
        clearSimulation,

        urbanSimToDuplicate,
        setUrbanSimToDuplicate,

        uploadedOverrideGeoJson,
        setUploadedOverrideGeoJson,
        runEntryPoints,
        setRunEntryPoints,
        spotProbability,
        setSpotProbability,
        spotProbabilityError,
      }}
    >
      {children}
    </SimulationsContext.Provider>
  );
};
